import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download, RefreshCw, BarChart3, AlertTriangle,
  Copy, UserPlus, Search as SearchIcon, ListChecks,
} from "lucide-react";
import { api } from "@/lib/api";
import { getStoredToken, getStoredOutlet } from "@/lib/authStorage";
import { TID } from "@/constants/testIds";
import { useSession } from "@/contexts/SessionContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatAmount, formatDate } from "@/lib/format";

// ---- Date helpers -------------------------------------------------------
function startOfTodayIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}
function nowIso() { return new Date().toISOString(); }
function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}
function isoToInputDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function inputDateToFromIso(s) {
  if (!s) return null;
  const d = new Date(s + "T00:00:00");
  return d.toISOString();
}
function inputDateToToIso(s) {
  if (!s) return null;
  const d = new Date(s + "T23:59:59.999");
  return d.toISOString();
}

const RANGES = [
  { key: "today", label: "Today", from: startOfTodayIso, to: nowIso },
  { key: "7d",    label: "Last 7 Days",  from: () => daysAgoIso(6), to: nowIso },
  { key: "30d",   label: "Last 30 Days", from: () => daysAgoIso(29), to: nowIso },
  { key: "custom", label: "Custom", from: () => null, to: () => null },
];

// ---- Format helpers -----------------------------------------------------
function pct(rate) {
  if (rate === null || rate === undefined) return "—";
  return `${(rate * 100).toFixed(1)}%`;
}
function secs(v) {
  if (v === null || v === undefined) return "—";
  if (v < 60) return `${v.toFixed(0)}s`;
  if (v < 3600) return `${(v / 60).toFixed(1)}m`;
  return `${(v / 3600).toFixed(1)}h`;
}

// ---- Metric card --------------------------------------------------------
function MetricCard({ tid, label, primary, secondary, accent, icon: Icon, hint }) {
  return (
    <div
      data-testid={tid}
      className="rounded-md border border-archival bg-archival-surface p-4 flex flex-col gap-1"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wider text-archival-muted">
          {label}
        </span>
        {Icon && <Icon size={14} className={accent || "text-archival-muted"} />}
      </div>
      <div className="text-[26px] font-semibold text-archival-primary leading-tight font-mono">
        {primary}
      </div>
      {secondary && (
        <div className="text-[12px] text-archival-secondary">{secondary}</div>
      )}
      {hint && (
        <div className="text-[10px] text-archival-muted italic mt-1">{hint}</div>
      )}
    </div>
  );
}

// ---- Drilldown bucket meta ---------------------------------------------
const DRILLDOWN_BUCKETS = [
  { key: "NEEDS_REVIEW",         label: "Needs Review",       icon: ListChecks, color: "text-amber-600" },
  { key: "OCR_FAILED",           label: "OCR Failed",         icon: AlertTriangle, color: "text-red-700" },
  { key: "VENDOR_LOOKUP_FAILED", label: "Vendor Lookup Failed", icon: SearchIcon, color: "text-red-700" },
  { key: "HARD_DUPLICATE",       label: "Hard Duplicate",     icon: Copy, color: "text-red-700" },
  { key: "NEW_VENDOR_REQUIRED",  label: "New Vendor Required", icon: UserPlus, color: "text-archival-secondary" },
];

export default function ValidationDashboard() {
  const navigate = useNavigate();
  const { outletIds, outlets, org, user, currentOutletId } = useSession();
  const outletList = (outlets && outlets.length ? outlets : outletIds.map((id) => ({ id, name: id })));

  const [rangeKey, setRangeKey] = useState("7d");
  const [customFromStr, setCustomFromStr] = useState(isoToInputDate(daysAgoIso(6)));
  const [customToStr, setCustomToStr] = useState(isoToInputDate(nowIso()));
  const [outletId, setOutletId] = useState("ALL");

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [activeBucket, setActiveBucket] = useState("NEEDS_REVIEW");
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Compute window iso strings for the chosen range
  const { fromIso, toIso } = useMemo(() => {
    if (rangeKey === "custom") {
      return {
        fromIso: inputDateToFromIso(customFromStr),
        toIso: inputDateToToIso(customToStr),
      };
    }
    const r = RANGES.find((x) => x.key === rangeKey);
    return { fromIso: r.from(), toIso: r.to() };
  }, [rangeKey, customFromStr, customToStr]);

  const baseParams = useMemo(() => {
    const p = {};
    if (fromIso) p.from = fromIso;
    if (toIso) p.to = toIso;
    if (outletId && outletId !== "ALL") p.outlet_id = outletId;
    return p;
  }, [fromIso, toIso, outletId]);

  // Fetch metrics whenever filter inputs change
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await api.validationMetrics(baseParams);
        if (!cancelled) setMetrics(d);
      } catch (e) {
        if (!cancelled) setError(
          e?.response?.data?.detail?.message || e.message || "Failed to load metrics"
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [baseParams, refreshTick]);

  // Fetch drilldown whenever bucket or filters change
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setDrilldownLoading(true);
      try {
        const d = await api.validationDrilldown({ ...baseParams, bucket: activeBucket });
        if (!cancelled) setDrilldown(d);
      } catch {
        if (!cancelled) setDrilldown(null);
      } finally {
        if (!cancelled) setDrilldownLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [baseParams, activeBucket, refreshTick]);

  const triggerRefresh = () => setRefreshTick((t) => t + 1);

  const counters = metrics?.counters || {};
  const rates = metrics?.rates || {};
  const averages = metrics?.averages_seconds || {};
  const dd = metrics?.drilldown_counts || {};
  const samples = metrics?.samples || {};

  const csvHref = api.validationCsvUrl(baseParams);
  const onDownloadCsv = async (e) => {
    // We have to hit the endpoint with auth headers — can't rely on
    // the browser to send Bearer. Use fetch + blob.
    e.preventDefault();
    try {
      const token = getStoredToken();
      const outletStored = getStoredOutlet();
      const headers = { Authorization: `Bearer ${token}` };
      if (outletStored) headers["X-Selected-Outlet-Id"] = outletStored;
      const resp = await fetch(csvHref, { headers });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const dl = document.createElement("a");
      dl.href = URL.createObjectURL(blob);
      dl.download = `bulk_capture_validation_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(dl);
      dl.click();
      dl.remove();
    } catch (err) {
      setError(err.message || "CSV export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid={TID.validationPage}>
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <BarChart3 size={20} className="text-archival-secondary" />
          <h1 className="text-[28px] font-semibold text-archival-primary leading-tight tracking-tight">
            Engine health
          </h1>
        </div>
        <p className="text-[13px] text-archival-secondary">
          Read-only operational view of the 1OS Bills engine. Use this to validate engine
          quality on real-world bills before enabling Phase 8 (Bulk Confirm).
        </p>
        <dl
          data-testid="tenant-identifiers"
          className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1 text-[11px] text-archival-muted"
        >
          <div className="min-w-0">
            <dt className="uppercase tracking-wide">Organisation ID</dt>
            <dd data-testid="tenant-id-org" className="font-mono break-all text-archival-secondary">{org?.id || "—"}</dd>
          </div>
          <div className="min-w-0">
            <dt className="uppercase tracking-wide">Outlet ID</dt>
            <dd data-testid="tenant-id-outlet" className="font-mono break-all text-archival-secondary">{currentOutletId || "—"}</dd>
          </div>
          <div className="min-w-0">
            <dt className="uppercase tracking-wide">User ID</dt>
            <dd data-testid="tenant-id-user" className="font-mono break-all text-archival-secondary">{user?.id || "—"}</dd>
          </div>
        </dl>
      </header>

      {/* Filter strip */}
      <div className="rounded-md border border-archival bg-archival-surface px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          {RANGES.map((rng) => (
            <Button
              key={rng.key}
              size="sm"
              variant={rangeKey === rng.key ? "default" : "outline"}
              className={`h-8 text-[12px] ${rangeKey === rng.key ? "" : "border-archival bg-white"}`}
              data-testid={
                rng.key === "today" ? TID.validationRangeToday
                : rng.key === "7d" ? TID.validationRange7d
                : rng.key === "30d" ? TID.validationRange30d
                : TID.validationRangeCustom
              }
              onClick={() => setRangeKey(rng.key)}
            >
              {rng.label}
            </Button>
          ))}
        </div>

        {rangeKey === "custom" && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={customFromStr}
              onChange={(e) => setCustomFromStr(e.target.value)}
              data-testid={TID.validationFromDate}
              className="h-8 w-[150px] text-[12px] border-archival bg-white"
            />
            <span className="text-[12px] text-archival-muted">→</span>
            <Input
              type="date"
              value={customToStr}
              onChange={(e) => setCustomToStr(e.target.value)}
              data-testid={TID.validationToDate}
              className="h-8 w-[150px] text-[12px] border-archival bg-white"
            />
          </div>
        )}

        <Select value={outletId} onValueChange={setOutletId}>
          <SelectTrigger
            data-testid={TID.validationOutletSelect}
            className="w-[220px] h-8 border-archival bg-white text-[12px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL" data-testid={TID.validationOutletAll}>All my outlets</SelectItem>
            {outletList.map((o) => (
              <SelectItem key={o.id} value={o.id} data-testid={TID.validationOutletOption(o.id)}>
                {o.name || o.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <Button
          variant="outline"
          size="sm"
          data-testid={TID.validationRefresh}
          onClick={triggerRefresh}
          className="h-8 border-archival bg-white text-[12px]"
        >
          <RefreshCw size={12} className="mr-1.5" />
          Refresh
        </Button>
        <Button
          variant="outline"
          size="sm"
          data-testid={TID.validationExportCsv}
          onClick={onDownloadCsv}
          className="h-8 border-archival bg-white text-[12px]"
        >
          <Download size={12} className="mr-1.5" />
          Export CSV
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-[13px]">
          {error}
        </div>
      )}

      {/* Metric grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <MetricCard
          tid={TID.validationMetric("bills_uploaded")}
          label="Bills Uploaded"
          primary={loading ? "…" : counters.bills_uploaded ?? 0}
          secondary={`in window`}
        />
        <MetricCard
          tid={TID.validationMetric("ocr_success_rate")}
          label="OCR Success Rate"
          primary={pct(rates.ocr_success_rate)}
          secondary={`${counters.ocr_success ?? 0} of ${(counters.ocr_success ?? 0) + (counters.ocr_failed ?? 0)} completed`}
          accent="text-emerald-600"
        />
        <MetricCard
          tid={TID.validationMetric("ocr_failure_rate")}
          label="OCR Failure Rate"
          primary={pct(rates.ocr_failure_rate)}
          secondary={`${counters.ocr_failed ?? 0} failed`}
          accent="text-red-700"
        />
        <MetricCard
          tid={TID.validationMetric("auto_vendor_match_rate")}
          label="Auto Vendor Match"
          primary={pct(rates.auto_vendor_match_rate)}
          secondary={`${counters.auto_vendor_match ?? 0} EXACT match`}
          hint="Matched an existing canonical vendor"
        />
        <MetricCard
          tid={TID.validationMetric("suggested_vendor_match_rate")}
          label="Suggested Match"
          primary={pct(rates.suggested_vendor_match_rate)}
          secondary={`${counters.suggested_vendor_match ?? 0} suggested/ambiguous`}
        />
        <MetricCard
          tid={TID.validationMetric("new_vendor_rate")}
          label="New Vendor Rate"
          primary={pct(rates.new_vendor_rate)}
          secondary={`${counters.new_vendor ?? 0} no match`}
        />
        <MetricCard
          tid={TID.validationMetric("duplicate_bill_rate")}
          label="Duplicate Bill Rate"
          primary={pct(rates.duplicate_bill_rate)}
          secondary={`${counters.duplicate_bill ?? 0} HARD_DUPLICATE`}
          accent="text-red-700"
        />
        <MetricCard
          tid={TID.validationMetric("bills_sent_to_ec")}
          label="Bills Confirmed"
          primary={counters.bills_sent_to_ec ?? counters.bills_confirmed ?? 0}
          secondary={`${pct(rates.sent_to_ec_rate ?? rates.confirmed_rate)} of uploaded`}
          accent="text-emerald-600"
        />
        <MetricCard
          tid={TID.validationMetric("bills_rejected")}
          label="Bills Rejected"
          primary={counters.bills_rejected ?? 0}
          secondary={`${pct(rates.rejected_rate)} of uploaded`}
        />
        <MetricCard
          tid={TID.validationMetric("avg_ocr")}
          label="Avg OCR Processing Time"
          primary={secs(averages.ocr_processing)}
          secondary={`n = ${samples.ocr_processing_n ?? 0}`}
        />
        <MetricCard
          tid={TID.validationMetric("avg_review")}
          label="Avg Review Time"
          primary={secs(averages.review_time)}
          secondary={`n = ${samples.review_time_n ?? 0}`}
          hint="OCR done → confirm starts"
        />
        <MetricCard
          tid={TID.validationMetric("avg_confirm")}
          label="Avg Confirm Time"
          primary={secs(averages.confirm_time)}
          secondary={`n = ${samples.confirm_time_n ?? 0}`}
          hint="confirm start → bill linked"
        />
      </div>

      {/* Drilldowns */}
      <section className="space-y-2">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-[16px] font-semibold text-archival-primary">Drilldowns</h2>
            <p className="text-[12px] text-archival-secondary">
              Where operators are spending time / where the engine struggles.
            </p>
          </div>
        </div>
        <div className="border-b border-archival flex gap-1 px-1 flex-wrap">
          {DRILLDOWN_BUCKETS.map((b) => {
            const n = dd[b.key] || 0;
            const isActive = activeBucket === b.key;
            const Ic = b.icon;
            return (
              <button
                key={b.key}
                data-testid={TID.validationDrilldownTab(b.key)}
                onClick={() => setActiveBucket(b.key)}
                className={`py-2.5 px-3 text-[13px] font-medium border-b-2 transition-all flex items-center gap-1.5 ${
                  isActive
                    ? "border-action text-archival-primary"
                    : "border-transparent text-archival-secondary hover:text-archival-primary"
                }`}
                style={isActive ? { borderColor: "var(--action)" } : undefined}
              >
                <Ic size={13} className={b.color} />
                {b.label}
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] px-1.5 h-[18px] rounded-full text-[10px] font-semibold bg-archival-soft text-archival-muted">
                  {n}
                </span>
              </button>
            );
          })}
        </div>

        <div className="rounded-md border border-archival bg-archival-surface overflow-hidden">
          {drilldownLoading && !drilldown ? (
            <div className="py-12 text-center text-archival-muted text-[13px]">Loading…</div>
          ) : !drilldown || drilldown.rows.length === 0 ? (
            <div className="py-12 text-center text-archival-muted text-[13px]">
              Nothing in this bucket for the selected window.
            </div>
          ) : (
            <ul>
              {drilldown.rows.map((row) => (
                <li
                  key={row.id}
                  data-testid={TID.validationDrilldownRow(row.id)}
                  className="grid grid-cols-12 items-center gap-3 px-4 h-[62px] border-b border-archival last:border-b-0 hover:bg-archival-soft transition-colors cursor-pointer"
                  onClick={() => navigate(`/candidates/${row.id}`)}
                >
                  <div className="col-span-3 min-w-0">
                    <div className="text-[13px] font-semibold text-archival-primary truncate">
                      {row.suggested_vendor_name || row.extracted_vendor_name || (
                        <span className="text-archival-muted italic font-normal">Vendor unresolved</span>
                      )}
                    </div>
                    <div className="text-[11px] text-archival-muted truncate">{row.original_filename}</div>
                  </div>
                  <div className="col-span-2 text-[12px] font-mono text-archival-primary">
                    {row.bill_number || <span className="text-archival-muted">—</span>}
                  </div>
                  <div className="col-span-2 text-[12px] font-mono text-archival-secondary">
                    {formatDate(row.bill_date || row.created_at)}
                  </div>
                  <div className="col-span-2 text-[13px] font-mono font-semibold text-archival-primary">
                    {formatAmount(row.total_amount, row.currency)}
                  </div>
                  <div className="col-span-2 text-[11px] text-archival-secondary truncate" title={row.error_message || row.status}>
                    {row.error_message
                      || row.vendor_resolution_state
                      || row.status}
                  </div>
                  <div className="col-span-1 text-right">
                    <span className="text-[11px] font-mono text-archival-muted">{row.outlet_id?.slice(-6)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
